import dayjs, { Dayjs } from "dayjs";
import { SessionInterface } from "../models/Session";

/** Venue-local wall-clock rules for the bulk check-in SMS, mirroring ConsolidatedFlowHelper. */
const CHECK_IN_SMS_SAME_DAY_FROM_HOUR = 11;
const CHECK_IN_SMS_SAME_DAY = { hour: 8, minute: 45 };
const CHECK_IN_SMS_EVENING_BEFORE = { hour: 17, minute: 30 };

const DEFAULT_LAST_MIN_OPENS_HOURS_BEFORE_CHECK_IN = 72;

/**
 * When the bulk check-in SMS goes out — the anchor the whole schedule hangs off.
 *
 * `smsSendingDateTime` wins when the admin set one; otherwise it is derived from the show
 * start, exactly as `ConsolidatedFlowHelper.GetCheckInStartTime` does: a show at or before
 * 11:00 is texted about at 17:30 the evening before, anything later at 08:45 on the day.
 *
 * An unset DateTimeOffset arrives from the API as year 0001, which read literally would put
 * every session past its check-in send — so anything implausibly old counts as unset.
 */
export const getCheckInSmsMoment = (session?: SessionInterface | null): Dayjs | null => {
  if (session?.smsSendingDateTime) {
    const explicit = dayjs(session.smsSendingDateTime);
    if (explicit.isValid() && explicit.year() > 1900) {
      return explicit;
    }
  }

  if (!session?.showStartTime) {
    return null;
  }

  const showStart = dayjs(session.showStartTime);
  if (!showStart.isValid()) {
    return null;
  }

  const timeOfDay =
    showStart.hour() < CHECK_IN_SMS_SAME_DAY_FROM_HOUR ? CHECK_IN_SMS_EVENING_BEFORE : CHECK_IN_SMS_SAME_DAY;
  const day = showStart.hour() < CHECK_IN_SMS_SAME_DAY_FROM_HOUR ? showStart.subtract(1, "day") : showStart;

  return day.hour(timeOfDay.hour).minute(timeOfDay.minute).second(0).millisecond(0);
};

/**
 * Whether a session has entered its last-min phase (TTT-688 §4.7).
 *
 * From the activation moment onwards a new application does not join the ordinary ticketing
 * queue: it joins the last min list, and any offer that follows is a short one. Several
 * screens have to change what they say because of it — the apply CTA, a full session's
 * ribbon, and the warning about inviting a guest who has no account — so the test lives in
 * one place rather than being re-derived next to each of them.
 *
 * DERIVED from the check-in send, not read off `compressedDateTime`. That column belongs to
 * the legacy flow now and is null on every consolidated session, so testing it here reported
 * "not in the last min phase" for the entire life of every consolidated session — the apply
 * button and the ribbon both said the wrong thing.
 *
 * `hoursBeforeCheckIn` should come from `GET /v1/sessions/defaults`
 * (`lastMinListOpensHoursBeforeCheckIn`) wherever the caller already has it; the default
 * mirrors the shipped schedule for callers that do not.
 */
export const isSessionInLastMinPhase = (
  session?: SessionInterface | null,
  hoursBeforeCheckIn: number = DEFAULT_LAST_MIN_OPENS_HOURS_BEFORE_CHECK_IN
): boolean => {
  if (!session?.consolidatedFlowEnabled) {
    return false;
  }

  const checkIn = getCheckInSmsMoment(session);
  if (!checkIn) {
    return false;
  }

  return checkIn.subtract(hoursBeforeCheckIn, "hour").isBefore(dayjs());
};
